import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Shield, LogOut, RefreshCw, Trash2, Mail, Phone, Briefcase, Heart, ExternalLink,
  Check, X, Star as StarIcon, Inbox, MessageSquare,
} from "lucide-react";
import api from "@/lib/api";
import { StarRow, serviceLabel, formatLocation } from "@/components/review-utils";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("submissions");
  const email = localStorage.getItem("ilg_admin_email") || "Admin";

  const logout = useCallback(() => {
    localStorage.removeItem("ilg_admin_token");
    localStorage.removeItem("ilg_admin_email");
    nav("/admin/login");
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem("ilg_admin_token")) nav("/admin/login");
  }, [nav]);

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0D0D0D] grain text-white">
      <header className="border-b border-white/5 bg-black/40 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#D4AF37]/60 flex items-center justify-center">
              <Shield className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.32em] text-[#D4AF37]">IMPACT LEGACY</div>
              <div className="text-[10px] tracking-[0.32em] text-white/60">ADMIN PORTAL</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/60 hover:text-[#D4AF37] transition-colors">
              View Site <ExternalLink className="w-3.5 h-3.5" />
            </Link>
            <span className="hidden md:block text-xs text-white/40">{email}</span>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 border border-white/10 hover:border-[#D4AF37]/50 hover:text-[#D4AF37] px-4 py-2 text-[11px] tracking-[0.22em] uppercase transition-all">
              <LogOut className="w-3.5 h-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
        <div className="overline mb-4">— Dashboard</div>
        <h1 className="font-serif text-4xl md:text-5xl text-white mb-10">Admin Console</h1>

        <div className="flex flex-wrap items-center gap-2 mb-12 border-b border-white/5">
          {[
            { k: "submissions", label: "Contact Submissions", Icon: Inbox },
            { k: "reviews", label: "Reviews", Icon: MessageSquare },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`px-5 py-3 text-[11px] tracking-[0.22em] uppercase border-b-2 -mb-px inline-flex items-center gap-2 transition-all ${
                tab === t.k ? "border-[#D4AF37] text-[#D4AF37]" : "border-transparent text-white/55 hover:text-white"
              }`}
            >
              <t.Icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "submissions" ? (
          <SubmissionsTab onUnauthorized={logout} />
        ) : (
          <ReviewsTab onUnauthorized={logout} />
        )}
      </main>
    </div>
  );
}

function SubmissionsTab({ onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/submissions");
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in.");
        onUnauthorized();
      } else {
        toast.error("Failed to load submissions");
      }
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Delete this submission?")) return;
    try {
      await api.delete(`/admin/submissions/${id}`);
      setItems((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filtered = items.filter((s) => (filter === "all" ? true : s.interest === filter));
  const coverageCount = items.filter((s) => s.interest === "coverage").length;
  const careerCount = items.filter((s) => s.interest === "career").length;

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        {[
          { label: "Total Submissions", v: items.length, Icon: Mail },
          { label: "Coverage Inquiries", v: coverageCount, Icon: Heart },
          { label: "Career Inquiries", v: careerCount, Icon: Briefcase },
        ].map(({ label, v, Icon }) => (
          <div key={label} className="card-luxury p-8 flex items-center justify-between">
            <div>
              <div className="overline mb-2">{label}</div>
              <div className="font-serif text-5xl text-white">{v}</div>
            </div>
            <div className="w-12 h-12 border border-[#D4AF37]/40 flex items-center justify-center">
              <Icon className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-2">
          {[{ k: "all", label: "All" }, { k: "coverage", label: "Coverage" }, { k: "career", label: "Career" }].map((t) => (
            <button
              key={t.k}
              data-testid={`filter-${t.k}`}
              onClick={() => setFilter(t.k)}
              className={`px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase border transition-all ${
                filter === t.k ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button data-testid="admin-refresh" onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/60 hover:text-[#D4AF37] transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div data-testid="submissions-list" className="space-y-4">
        {loading && items.length === 0 && (
          <div className="card-luxury p-10 text-center text-white/50">Loading submissions...</div>
        )}
        {!loading && filtered.length === 0 && (
          <div data-testid="empty-state" className="card-luxury p-16 text-center">
            <div className="overline mb-4">— No Submissions Yet</div>
            <p className="text-white/55">When prospects reach out, their inquiries will appear here.</p>
          </div>
        )}
        {filtered.map((s) => (
          <article key={s.id} data-testid={`sub-${s.id}`} className="card-luxury p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-3">
              <div className="overline mb-2">{fmtDate(s.created_at)}</div>
              <div className={`inline-block px-3 py-1 text-[10px] tracking-[0.22em] uppercase border ${s.interest === "coverage" ? "border-[#008B8B] text-[#008B8B]" : "border-[#D4AF37] text-[#D4AF37]"}`}>
                {s.interest === "coverage" ? "Coverage" : "Career"}
              </div>
            </div>
            <div className="md:col-span-7">
              <h3 className="font-serif text-2xl text-white mb-3">{s.name}</h3>
              <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-white/70 mb-4">
                <a href={`mailto:${s.email}`} className="inline-flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
                  <Mail className="w-3.5 h-3.5" /> {s.email}
                </a>
                {s.phone && (
                  <a href={`tel:${s.phone}`} className="inline-flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
                    <Phone className="w-3.5 h-3.5" /> {s.phone}
                  </a>
                )}
              </div>
              {s.message && <p className="text-white/65 leading-relaxed text-sm whitespace-pre-wrap">{s.message}</p>}
            </div>
            <div className="md:col-span-2 flex md:justify-end">
              <button data-testid={`sub-delete-${s.id}`} onClick={() => remove(s.id)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/40 hover:text-red-400 transition-colors">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}

function ReviewsTab({ onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === "all" ? {} : { status_filter: filter };
      const { data } = await api.get("/admin/reviews", { params });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired.");
        onUnauthorized();
      } else {
        toast.error("Failed to load reviews");
      }
    } finally {
      setLoading(false);
    }
  }, [filter, onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, payload) => {
    try {
      const { data } = await api.patch(`/admin/reviews/${id}`, payload);
      setItems((arr) => arr.map((r) => (r.id === id ? data : r)));
      toast.success("Updated");
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setItems((arr) => arr.filter((r) => r.id !== id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const counts = {
    pending: items.filter((r) => r.status === "pending").length,
    approved: items.filter((r) => r.status === "approved").length,
    featured: items.filter((r) => r.featured).length,
    avg: items.length ? (items.reduce((a, r) => a + r.rating, 0) / items.length).toFixed(1) : "—",
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
        {[
          { label: "Pending Review", v: counts.pending, Icon: Inbox },
          { label: "Approved", v: counts.approved, Icon: Check },
          { label: "Featured", v: counts.featured, Icon: StarIcon },
          { label: "Average Rating", v: counts.avg, Icon: StarIcon },
        ].map(({ label, v, Icon }) => (
          <div key={label} className="card-luxury p-6 flex items-center justify-between">
            <div>
              <div className="overline mb-1">{label}</div>
              <div className="font-serif text-4xl text-white">{v}</div>
            </div>
            <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Icon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div className="flex flex-wrap items-center gap-2">
          {[
            { k: "pending", label: "Pending" },
            { k: "approved", label: "Approved" },
            { k: "rejected", label: "Rejected" },
            { k: "all", label: "All" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`review-filter-${t.k}`}
              onClick={() => setFilter(t.k)}
              className={`px-5 py-2.5 text-[11px] tracking-[0.22em] uppercase border transition-all ${
                filter === t.k ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button data-testid="admin-refresh-reviews" onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-white/60 hover:text-[#D4AF37] transition-colors">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div data-testid="reviews-admin-list" className="space-y-4">
        {loading && items.length === 0 && (
          <div className="card-luxury p-10 text-center text-white/50">Loading reviews...</div>
        )}
        {!loading && items.length === 0 && (
          <div data-testid="reviews-admin-empty" className="card-luxury p-16 text-center">
            <div className="overline mb-4">— No Reviews</div>
            <p className="text-white/55">Reviews submitted by clients will appear here for moderation.</p>
          </div>
        )}
        {items.map((r) => (
          <article key={r.id} data-testid={`admin-review-${r.id}`} className="card-luxury p-8 grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-3">
              <div className="overline mb-2">{fmtDate(r.created_at)}</div>
              <StarRow value={r.rating} />
              <div className="mt-3 flex flex-wrap gap-2">
                <span className={`inline-block px-3 py-1 text-[10px] tracking-[0.22em] uppercase border ${
                  r.status === "approved" ? "border-emerald-400 text-emerald-400" :
                  r.status === "rejected" ? "border-red-400 text-red-400" :
                  "border-[#D4AF37] text-[#D4AF37]"
                }`}>
                  {r.status}
                </span>
                {r.featured && (
                  <span className="inline-block px-3 py-1 text-[10px] tracking-[0.22em] uppercase border border-[#008B8B] text-[#008B8B]">Featured</span>
                )}
              </div>
            </div>
            <div className="md:col-span-6">
              <h3 className="font-serif text-2xl text-white mb-2">{r.name}</h3>
              <div className="text-xs text-white/40 mb-3 tracking-wide">
                {[formatLocation(r), serviceLabel(r.service)].filter(Boolean).join(" · ")}
              </div>
              <p className="text-white/70 leading-relaxed text-sm whitespace-pre-wrap">"{r.text}"</p>
            </div>
            <div className="md:col-span-3 flex md:flex-col md:items-end gap-2 flex-wrap">
              {r.status !== "approved" && (
                <button data-testid={`review-approve-${r.id}`} onClick={() => update(r.id, { status: "approved" })} className="inline-flex items-center gap-2 px-3 py-2 border border-emerald-400/40 text-emerald-400 hover:bg-emerald-400 hover:text-black text-[10px] tracking-[0.22em] uppercase transition-all">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
              )}
              {r.status !== "rejected" && (
                <button data-testid={`review-reject-${r.id}`} onClick={() => update(r.id, { status: "rejected" })} className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-white/60 hover:border-red-400 hover:text-red-400 text-[10px] tracking-[0.22em] uppercase transition-all">
                  <X className="w-3.5 h-3.5" /> Reject
                </button>
              )}
              <button data-testid={`review-feature-${r.id}`} onClick={() => update(r.id, { featured: !r.featured })} className={`inline-flex items-center gap-2 px-3 py-2 border text-[10px] tracking-[0.22em] uppercase transition-all ${
                r.featured ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/15 text-white/60 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              }`}>
                <StarIcon className="w-3.5 h-3.5" /> {r.featured ? "Unfeature" : "Feature"}
              </button>
              <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r.id)} className="inline-flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-white/40 hover:text-red-400 transition-colors mt-2">
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
